import React from 'react';
import { SelectGroup, SelectItem, SelectLabel, SelectSeparator } from '../ui/select';
import { Button } from '../ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

const labelOf = (p) => `${p.project_number ? `${p.project_number} · ` : ''}${p.name}`;

// True when a YYYY-MM-DD date falls outside the project's optional start/end window.
export const isOutsideProjectDates = (p, date) =>
  !!(p && date && ((p.start_date && date < p.start_date) || (p.end_date && date > p.end_date)));

// Grouped project options for the Add/Edit session dialogs:
//   1. projects this worker recently clocked into (from the loaded sessions)
//   2. every active company project
//   3. closed projects (backfilling hours after a project wrapped up)
// Also renders a visible error + Retry when the catalogue failed to load,
// instead of silently showing "No project" only.
export const ProjectOptions = ({ projects, projectsLoading, projectsError, onRetry, recentIds = [], testPrefix, forDate }) => {
  const list = projects || [];
  const recentSet = new Set(recentIds);
  const recent = list.filter((p) => recentSet.has(p.id));
  const active = list.filter((p) => !recentSet.has(p.id) && p.status !== 'closed');
  const closed = list.filter((p) => !recentSet.has(p.id) && p.status === 'closed');

  if (projectsError) {
    return (
      <div className="px-2 py-2 space-y-2" data-testid={`${testPrefix}-error`}>
        <p className="text-xs text-amber-400 flex items-start gap-1">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Couldn't load the project list. Check your connection and try again.
        </p>
        <Button size="sm" variant="outline" className="h-7 text-xs w-full" onClick={onRetry} data-testid={`${testPrefix}-retry`}>
          <RefreshCw className="w-3 h-3 mr-1" /> Retry loading projects
        </Button>
      </div>
    );
  }
  if (projectsLoading && list.length === 0) {
    return <p className="px-2 py-2 text-xs text-slate-400" data-testid={`${testPrefix}-loading`}>Loading projects…</p>;
  }
  if (list.length === 0) {
    return (
      <p className="px-2 py-2 text-xs text-slate-400" data-testid={`${testPrefix}-empty`}>
        No projects exist yet. Create one under Settings → Projects.
      </p>
    );
  }

  const group = (label, rows, key) => rows.length > 0 && (
    <SelectGroup key={key}>
      <SelectLabel className="text-[11px] uppercase tracking-wide text-slate-400">{label}</SelectLabel>
      {rows.map((p) => (
        <SelectItem key={p.id} value={p.id} data-testid={`${testPrefix}-${p.id}`}>
          {labelOf(p)}
          {p.customer_name ? <span className="ml-1 text-[10px] opacity-60">· {p.customer_name}</span> : null}
          {isOutsideProjectDates(p, forDate) ? <span className="ml-1 text-[10px] text-amber-400">· outside project dates</span> : null}
        </SelectItem>
      ))}
    </SelectGroup>
  );

  return (
    <>
      {recent.length > 0 && <SelectSeparator />}
      {group('Recent for this worker', recent, 'recent')}
      {(recent.length > 0 || active.length > 0) && <SelectSeparator />}
      {group('Active projects', active, 'active')}
      {closed.length > 0 && <SelectSeparator />}
      {group('Closed projects', closed, 'closed')}
    </>
  );
};
